import React, { useEffect, useMemo, useState } from 'react';
import meetingsAPI from '../../../../services/meetingsApi';
import { ActionItem, MeetingAttendee, PrepAction, PrepActionsResponse } from '../../../../types/meeting';

interface MeetingPrepActionsPanelProps {
  meetingId: string;
  attendees: MeetingAttendee[];
  /** Secretaries see every prep task and may raise and withdraw them. */
  canManage?: boolean;
  /** Signed-in user, so a secretary's own tasks are still marked as theirs. */
  currentUserId?: string | null;
}

const formatDue = (due?: string | null): string | null => {
  if (!due) return null;
  const parsed = new Date(due);
  return Number.isNaN(parsed.getTime())
    ? due // Due dates are free text in the minutes; show what was written.
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const ActionRow: React.FC<{ action: ActionItem; provenance?: string }> = ({ action, provenance }) => {
  const due = formatDue(action.due_date);
  return (
    <li className="prep-action-item">
      <p className="prep-action-description">{action.description}</p>
      <div className="prep-action-meta">
        {provenance && <span className="prep-action-source">{provenance}</span>}
        {due && <span className="prep-action-due">Due {due}</span>}
        {action.status && <span className="prep-action-status">{action.status}</span>}
      </div>
    </li>
  );
};

/**
 * A prep task, with the controls the viewer is actually entitled to use.
 *
 * "Mark done" appears only on the viewer's own task: a secretary is shown
 * everyone's so they can track progress, but completing someone else's work on
 * their behalf is not what tracking means.
 */
const PrepActionRow: React.FC<{
  action: PrepAction;
  isMine: boolean;
  canManage: boolean;
  busy: boolean;
  onSetStatus: (status: 'open' | 'completed') => void;
  onDelete: () => void;
}> = ({ action, isMine, canManage, busy, onSetStatus, onDelete }) => {
  const due = formatDue(action.due_date);
  const done = action.status === 'completed';
  return (
    <li className={`prep-action-item${done ? ' prep-action-item-done' : ''}`}>
      <p className="prep-action-description">{action.description}</p>
      <div className="prep-action-meta">
        {!isMine && action.assignee_name && (
          <span className="prep-action-assignee">{action.assignee_name}</span>
        )}
        {due && <span className="prep-action-due">Due {due}</span>}
        <span className="prep-action-status">{done ? 'Done' : 'Open'}</span>
      </div>
      {(isMine || canManage) && (
        <div className="prep-action-controls">
          {isMine && (
            <button
              type="button"
              className="prep-action-btn"
              disabled={busy}
              onClick={() => onSetStatus(done ? 'open' : 'completed')}
            >
              {done ? 'Reopen' : 'Mark done'}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className="prep-action-btn prep-action-btn-danger"
              disabled={busy}
              onClick={onDelete}
            >
              Withdraw
            </button>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * "Actions assigned to you" for the meeting being prepared for.
 *
 * Absence is reported rather than implied. An empty list alongside unresolved
 * owners is not the same claim as an empty list with none, and a meeting with no
 * committee has not been searched for earlier actions at all — a board member
 * deciding they have nothing outstanding needs to know which of those they are
 * looking at.
 */
const MeetingPrepActionsPanel: React.FC<MeetingPrepActionsPanelProps> = ({
  meetingId,
  attendees,
  canManage = false,
  currentUserId
}) => {
  const [data, setData] = useState<PrepActionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draftDescription, setDraftDescription] = useState('');
  const [draftAssignee, setDraftAssignee] = useState('');
  const [draftDue, setDraftDue] = useState('');
  const [saving, setSaving] = useState(false);

  // Only linked attendees can be assigned to: an unlinked one has no account to
  // see the task with, so offering them would create an invisible assignment.
  const assignable = useMemo(
    () => attendees.filter((a) => Boolean(a.user_id)),
    [attendees]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    meetingsAPI
      .getPrepActions(meetingId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load your actions');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId, reloadKey]);

  // Refetch rather than patch state locally: a prep task can be closed by its
  // assignee elsewhere, so the server's list is the one worth trusting.
  const refresh = () => setReloadKey((k) => k + 1);

  const runMutation = async (id: string, mutate: () => Promise<unknown>) => {
    setBusyId(id);
    setMutationError(null);
    try {
      await mutate();
      refresh();
    } catch (err: unknown) {
      setMutationError(err instanceof Error ? err.message : 'Could not update the action');
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const description = draftDescription.trim();
    if (!description || !draftAssignee) return;

    setSaving(true);
    setMutationError(null);
    try {
      await meetingsAPI.createPrepAction(meetingId, {
        description,
        assignee_user_id: draftAssignee,
        due_date: draftDue || null
      });
      setDraftDescription('');
      setDraftDue('');
      setComposerOpen(false);
      refresh();
    } catch (err: unknown) {
      setMutationError(err instanceof Error ? err.message : 'Could not add the action');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="meeting-panel">
        <h3 className="meeting-panel-title">Actions assigned to you</h3>
        <div className="meeting-empty-hint">Loading your actions…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="meeting-panel">
        <h3 className="meeting-panel-title">Actions assigned to you</h3>
        <div className="prep-actions-error">{error || 'Failed to load your actions'}</div>
      </div>
    );
  }

  const prepAssigned = data.prep_assigned || [];
  const nothingAssigned =
    data.this_meeting.length === 0 && data.carried_over.length === 0 && prepAssigned.length === 0;

  return (
    <div className="meeting-panel">
      <h3 className="meeting-panel-title">Actions assigned to you</h3>

      {mutationError && <div className="prep-actions-error">{mutationError}</div>}

      {nothingAssigned && (
        <div className="meeting-empty-hint">
          {data.unresolved_owner_count > 0
            ? 'Nothing is assigned to you by name.'
            : 'Nothing is assigned to you for this meeting.'}
        </div>
      )}

      {prepAssigned.length > 0 && (
        <section className="prep-actions-group">
          {/* Labelled by whose tasks these are. A secretary is shown everyone's,
              and calling that list "yours" would be a lie they act on. */}
          <h4 className="prep-actions-group-title">
            {data.prep_assigned_is_all ? 'To do before this meeting (all attendees)' : 'To do before this meeting'}
          </h4>
          <ul className="prep-action-list">
            {prepAssigned.map((action) => (
              <PrepActionRow
                key={action.prep_action_id}
                action={action}
                isMine={Boolean(currentUserId) && action.assignee_user_id === currentUserId}
                canManage={canManage}
                busy={busyId === action.prep_action_id}
                onSetStatus={(status) =>
                  runMutation(action.prep_action_id, () =>
                    meetingsAPI.setPrepActionStatus(meetingId, action.prep_action_id, status))
                }
                onDelete={() =>
                  runMutation(action.prep_action_id, () =>
                    meetingsAPI.deletePrepAction(meetingId, action.prep_action_id))
                }
              />
            ))}
          </ul>
        </section>
      )}

      {canManage && (
        <div className="prep-action-composer">
          {!composerOpen && (
            <button type="button" className="prep-action-btn" onClick={() => setComposerOpen(true)}>
              Assign an action
            </button>
          )}

          {composerOpen && assignable.length === 0 && (
            // Assigning needs a linked attendee, so say why the form is absent
            // rather than presenting an empty dropdown that cannot be submitted.
            <p className="prep-actions-note">
              No attendee is linked to a user account yet, so there is nobody to assign an action to.
              Link attendees in the Attendees panel first.
            </p>
          )}

          {composerOpen && assignable.length > 0 && (
            <form className="prep-action-form" onSubmit={handleCreate}>
              <label className="sr-only" htmlFor="prep-action-description">Action</label>
              <textarea
                id="prep-action-description"
                className="prep-action-input"
                rows={2}
                placeholder="What needs doing before the meeting?"
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value)}
              />
              <label className="sr-only" htmlFor="prep-action-assignee">Assign to</label>
              <select
                id="prep-action-assignee"
                className="prep-action-input"
                value={draftAssignee}
                onChange={(e) => setDraftAssignee(e.target.value)}
              >
                <option value="">Assign to…</option>
                {assignable.map((a) => (
                  <option key={a.attendee_id} value={a.user_id as string}>{a.name}</option>
                ))}
              </select>
              <label className="sr-only" htmlFor="prep-action-due">Due date</label>
              <input
                id="prep-action-due"
                type="date"
                className="prep-action-input"
                value={draftDue}
                onChange={(e) => setDraftDue(e.target.value)}
              />
              <div className="prep-action-controls">
                <button
                  type="submit"
                  className="prep-action-btn"
                  disabled={saving || !draftDescription.trim() || !draftAssignee}
                >
                  {saving ? 'Adding…' : 'Add action'}
                </button>
                <button
                  type="button"
                  className="prep-action-btn"
                  disabled={saving}
                  onClick={() => setComposerOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {data.this_meeting.length > 0 && (
        <section className="prep-actions-group">
          <h4 className="prep-actions-group-title">From this meeting</h4>
          <ul className="prep-action-list">
            {data.this_meeting.map((a, i) => (
              <ActionRow key={a.action_id || i} action={a} />
            ))}
          </ul>
        </section>
      )}

      {data.carried_over.length > 0 && (
        <section className="prep-actions-group">
          <h4 className="prep-actions-group-title">Still outstanding from earlier meetings</h4>
          <ul className="prep-action-list">
            {data.carried_over.map((entry, i) => (
              <ActionRow
                key={entry.action.action_id || i}
                action={entry.action}
                provenance={entry.source.title}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Both notes describe a limit on what was searched, not a result. They
          stay visible even when actions were found, because the list being
          non-empty does not make it complete. */}
      {data.unresolved_owner_count > 0 && (
        <p className="prep-actions-note">
          {data.unresolved_owner_count} action{data.unresolved_owner_count === 1 ? '' : 's'} at this
          meeting {data.unresolved_owner_count === 1 ? 'has' : 'have'} an owner that isn’t linked to a
          person, so {data.unresolved_owner_count === 1 ? 'it isn’t' : 'they aren’t'} shown here. Ask
          the secretary to set the owner in the minutes.
        </p>
      )}

      {data.committee_missing && (
        <p className="prep-actions-note">
          This meeting has no committee set, so earlier meetings weren’t searched for outstanding
          actions.
        </p>
      )}
    </div>
  );
};

export default MeetingPrepActionsPanel;
